import React, { useCallback } from 'react';
import { Stack, Text } from '@adc/parallax-component-library';
import { differenceInYears, parseISO } from 'date-fns';
import { format } from 'date-fns';
import { CLOSE_MODAL, SHOW_MODAL } from 'Reducers/modal/types';
import i18n from 'Utilities/i18n';
import { store } from 'Utilities/store';
const useAccount = () => {
    const showEmailExistsMessage = useCallback((onClickSignIn) => {
        store.dispatch({
            type: SHOW_MODAL,
            modalProps: {
                testID: 'RegisterAccountInfo',
                details: (React.createElement(Stack, { gap: "$4" },
                    React.createElement(Text, { testID: "RegisterAccountInfo.accountExists", nativeID: "RegisterAccountInfo.accountExists" }, i18n.t('CreateAccountAccountInfo.modals.accountInfoErrors.emailExists', {
                        appName: i18n.t('Global.appNames.libreView'),
                    })),
                    React.createElement(Text, { testID: "RegisterAccountInfo.trySignIn", nativeID: "RegisterAccountInfo.trySignIn" }, i18n.t('CreateAccountAccountInfo.modals.accountInfoErrors.trySignIn')))),
                primaryAction: {
                    label: i18n.t('CreateAccountAccountInfo.modals.accountInfoErrors.signIn'),
                    onPress: () => {
                        store.dispatch({
                            type: CLOSE_MODAL,
                        });
                        onClickSignIn();
                    },
                },
                secondaryAction: {
                    label: i18n.t('CreateAccountAccountInfo.modals.accountInfoErrors.cancel'),
                    onPress: () => {
                        store.dispatch({
                            type: CLOSE_MODAL,
                        });
                    },
                },
                buttonLayout: 'column',
            },
        });
    }, []);
    const showExistingProUserError = useCallback(() => {
        store.dispatch({
            type: SHOW_MODAL,
            modalProps: {
                details: i18n.t('CreateAccountAccountInfo.modals.accountInfoErrors.emailExistsProfessional', { appName: i18n.t('Global.appNames.libreView') }),
            },
        });
    }, []);
    const validateEntriesLengthAndDisplayError = (items) => {
        const messages = [];
        for (const item of items) {
            if (item.count && item.value.length > item.count) {
                messages.push({ msgKey: item.msgKey, count: item.count });
            }
        }
        if (messages.length) {
            store.dispatch({
                type: SHOW_MODAL,
                modalProps: {
                    details: (React.createElement(Stack, { gap: "$4" }, messages?.map((message, i) => (React.createElement(Text, { key: i, testID: message.msgKey, nativeID: message.msgKey, type: "$bodyBase.default" }, i18n.t(message.msgKey, { count: message.count })))))),
                },
            });
            return false;
        }
        return true;
    };
    const dispatchServerErrorModal = useCallback(() => {
        store.dispatch({
            type: SHOW_MODAL,
            modalProps: {
                details: i18n.t('Global.modals.errorCommunicatingServer.body'),
                testID: 'ApiErrorModal',
            },
        });
    }, []);
    const dispatchPasswordValidationModal = useCallback((errorCode, limit) => {
        store.dispatch({
            type: SHOW_MODAL,
            modalProps: {
                details: (React.createElement(Stack, { gap: 4 },
                    React.createElement(Text, { color: "$text.100", type: "$bodyBase.default", nativeID: "CreateAccountAccountInfo.modals.accountInfoErrors.differentPasswordTitle", testID: "CreateAccountAccountInfo.modals.accountInfoErrors.differentPasswordTitle", marginBottom: "$1" }, i18n.t('CreateAccountAccountInfo.modals.accountInfoErrors.differentPasswordTitle')),
                    React.createElement(Text, { key: errorCode, color: "$text.100", type: "$bodyBase.default", nativeID: `CreateAccountAccountInfo.modals.accountInfoErrors.securityRules.${errorCode}`, testID: `CreateAccountAccountInfo.modals.accountInfoErrors.securityRules.${errorCode}`, marginBottom: 1 }, i18n.t(`CreateAccountAccountInfo.modals.accountInfoErrors.securityRules.${errorCode}`, {
                        count: limit ?? 0,
                    })))),
                testID: 'Auth',
            },
        });
    }, []);
    const showPasswordValidationError = useCallback((passwordRules, errors) => {
        if (!errors?.length) {
            dispatchServerErrorModal();
            return;
        }
        const errorCode = errors[0].error;
        const translationKey = `CreateAccountAccountInfo.modals.accountInfoErrors.securityRules.${errorCode}`;
        if (!i18n.exists(translationKey)) {
            dispatchServerErrorModal();
            return;
        }
        const limit = errorCode === 'max'
            ? passwordRules.find((item) => item.data.key === errorCode)?.data.value
            : passwordRules.find((item) => item.type === errorCode)?.data.limit;
        dispatchPasswordValidationModal(errorCode, limit);
    }, [dispatchPasswordValidationModal, dispatchServerErrorModal]);
    const validateIsMinor = useCallback((dateOfBirth, adultAge) => {
        const dob = parseISO(dateOfBirth);
        const age = differenceInYears(new Date(), dob);
        return age < adultAge;
    }, []);
    const validateDOBAndDisplayError = useCallback((dateOfBirth) => {
        const today = format(new Date(), 'yyyy-MM-dd');
        if (dateOfBirth > today) {
            store.dispatch({
                type: SHOW_MODAL,
                modalProps: {
                    details: i18n.t('CreateAccountPersonalInfo.modals.personalInfoErrors.dobError'),
                },
            });
            return false;
        }
        return true;
    }, []);
    const showMinorRestrictionMessage = useCallback((adultAge) => {
        store.dispatch({
            type: SHOW_MODAL,
            modalProps: {
                details: i18n.t('CreateAccountPersonalInfo.modals.personalInfoErrors.countryInvalidAge', { age: adultAge }),
            },
        });
    }, []);
    return {
        showEmailExistsMessage,
        showExistingProUserError,
        showPasswordValidationError,
        showMinorRestrictionMessage,
        validateEntriesLengthAndDisplayError,
        validateIsMinor,
        validateDOBAndDisplayError,
    };
};
export default useAccount;
